import type { Planet, TeamPlanetFlightsView } from '@kaeliux/shared';
import { authedPost } from './http';
import { normalizePlanet } from './planetNormalization';

/**
 * Team-planet service — every call is a Cloud Function POST, reads
 * included, same reasoning as `alliance.service.ts`: `players/{userId}/**`
 * (where a converted Team planet physically lives) is owner-only readable
 * via the client SDK, and other alliance members are never that owner.
 */

export async function getTeamPlanet(): Promise<Planet | null> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown | null }>('/getTeamPlanet', {});
    return teamPlanet ? normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id) : null;
}

/** Every flight to or from the alliance's Team planet — any member may read (issue #294 flight coordinator). */
export async function getTeamPlanetFlights(): Promise<TeamPlanetFlightsView> {
    const view = await authedPost<TeamPlanetFlightsView>('/getTeamPlanetFlights', {});
    return {
        ...view,
        flights: view.flights.map((flight) => ({
            ...flight,
            departedAt: new Date(flight.departedAt),
            arrivesAt: new Date(flight.arrivesAt),
            returnsAt: new Date(flight.returnsAt),
        })),
    };
}

/** Convert one of the leader's own planets into the alliance's Team planet. */
export async function convertToTeamPlanet(planetId: string): Promise<{ teamPlanetId: string }> {
    return authedPost<{ teamPlanetId: string }>('/convertToTeamPlanet', { planetId });
}

export async function upgradeTeamPlanetBuilding(buildingType: string, plotId?: string): Promise<Planet> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown }>(
        '/upgradeTeamPlanetBuilding',
        plotId ? { buildingType, plotId } : { buildingType },
    );
    return normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id);
}

export async function cancelTeamPlanetBuildingUpgrade(eventId: number): Promise<Planet> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown }>('/cancelTeamPlanetBuildingUpgrade', { eventId });
    return normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id);
}

export async function queueTeamPlanetScience(scienceId: string): Promise<Planet> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown }>('/queueTeamPlanetScience', { scienceId });
    return normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id);
}

export async function cancelTeamPlanetScience(eventId: number): Promise<Planet> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown }>('/cancelTeamPlanetScience', { eventId });
    return normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id);
}

export async function queueTeamPlanetShips(shipId: number, quantity: number): Promise<Planet> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown }>('/queueTeamPlanetShips', { shipId, quantity });
    return normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id);
}

export async function cancelTeamPlanetShipBuild(eventId: number): Promise<Planet> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown }>('/cancelTeamPlanetShipBuild', { eventId });
    return normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id);
}

export async function queueTeamPlanetShipUpgrade(shipId: number): Promise<Planet> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown }>('/queueTeamPlanetShipUpgrade', { shipId });
    return normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id);
}

export async function cancelTeamPlanetShipUpgrade(eventId: number): Promise<Planet> {
    const { teamPlanet } = await authedPost<{ teamPlanet: unknown }>('/cancelTeamPlanetShipUpgrade', { eventId });
    return normalizePlanet(teamPlanet, (teamPlanet as { id: string }).id);
}
